import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { Check, Close, Dustbin, Edit } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import {
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../Button';
import { Link } from '../../../Link';
import { TextInput } from '../../TextInput';
import { getSelectedNode } from '../RichTextArea.utils';

import styles from './FloatingLinkPopoverPlugin.module.scss';

function setFloatingElemPositionForLinkEditor(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement
): void {
  const scrollerElem = document.body.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = document.body.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.bottom;
  let left = targetRect.left;

  if (top + floatingElemRect.height > scrollerElem.scrollTop + window.innerHeight) {
    top = targetRect.top - floatingElemRect.height - 48;
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width;
  }

  top -= anchorElementRect.top + 16;
  left -= anchorElementRect.left;

  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

const FloatingLinkEditor = ({
  editor,
  isLink,
  setIsLink,
  isLinkEditOpen,
  setIsLinkEditOpen,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  isLinkEditOpen: boolean;
  setIsLinkEditOpen: Dispatch<boolean>;
}): JSX.Element | null => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('https://');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setLinkUrl(linkParent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
      if (isLinkEditOpen) {
        setEditedLinkUrl(linkUrl);
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement !== inputRef.current) {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem);
      }
      setLastSelection(null);
      setIsLinkEditOpen(false);
      setLinkUrl('');
    }

    return true;
  }, [editor, setIsLinkEditOpen, isLinkEditOpen, linkUrl]);

  useEffect(() => {
    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);
    document.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update);
    };
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus());
    }
  }, [isLinkEditOpen, isLink]);

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.update(() => {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, editedLinkUrl);
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
        });
      }
      setEditedLinkUrl('https://');
      setIsLinkEditOpen(false);
    }
  };

  useEffect(() => {
    if (!isLink) setIsLinkEditOpen(false);
  }, [isLink, setIsLinkEditOpen]);

  if (!isLink) return null;

  return (
    <div ref={editorRef} className={styles.popover}>
      {isLinkEditOpen ? (
        <>
          <TextInput
            ref={inputRef}
            size="small"
            value={editedLinkUrl}
            onChange={event => {
              setEditedLinkUrl(event.currentTarget.value);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleLinkSubmission();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setIsLinkEditOpen(false);
              }
            }}
          />
          <Button variant="tertiary" size="medium" onClick={() => setIsLinkEditOpen(false)}>
            <Close />
          </Button>
          <Button
            variant="tertiary"
            size="medium"
            onMouseDown={event => event.preventDefault()}
            onClick={event => {
              event.preventDefault();
              handleLinkSubmission();
            }}
          >
            <Check />
          </Button>
        </>
      ) : (
        <>
          <Link to={linkUrl} className={styles.link} target="_blank" rel="noopener noreferrer">
            {linkUrl}
          </Link>
          <Button
            variant="tertiary"
            size="medium"
            onMouseDown={event => event.preventDefault()}
            onClick={event => {
              event.preventDefault();
              setEditedLinkUrl(linkUrl);
              setIsLinkEditOpen(true);
            }}
          >
            <Edit />
          </Button>
          <Button
            variant="tertiary"
            size="medium"
            onMouseDown={event => event.preventDefault()}
            onClick={event => {
              event.preventDefault();
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }}
          >
            <Dustbin />
          </Button>
        </>
      )}
    </div>
  );
};

export const FloatingLinkEditorPlugin = ({
  isLinkEditOpen,
  setIsLinkEditOpen,
}: {
  isLinkEditOpen: boolean;
  setIsLinkEditOpen: Dispatch<boolean>;
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();

  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode);
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter(node => !$isLineBreakNode(node))
          .find(node => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode &&
                (!autoLinkNode.is(focusAutoLinkNode) || autoLinkNode.getIsUnlinked()))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        payload => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      setIsLink={setIsLink}
      isLinkEditOpen={isLinkEditOpen}
      setIsLinkEditOpen={setIsLinkEditOpen}
    />,
    document.body
  );
};
